import React from "react";
import { topSellers } from "../mock";
import { useCart, formatPrice } from "../context/CartContext";
import Stars from "./Stars";
import { ShoppingCart, Check } from "lucide-react";
import useReveal from "../hooks/useReveal";

function TopSellerCard({ p }) {
  const { addItem } = useCart();
  const discount = p.compareAt ? Math.round((1 - p.price / p.compareAt) * 100) : 0;
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
      <div className="relative aspect-square overflow-hidden bg-gray-50 p-5">
        <span className="absolute left-3 top-3 z-10 rounded-full bg-brand px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
          {p.badge}
        </span>
        {discount > 0 && (
          <span className="absolute right-3 top-3 z-10 rounded-full bg-navy px-2.5 py-1 text-[11px] font-bold text-white">
            -{discount}%
          </span>
        )}
        <img
          src={p.image}
          alt={p.name}
          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-navy/40">{p.brand}</p>
        <h3 className="mt-1 min-h-[44px] text-sm font-bold leading-snug text-navy">{p.name}</h3>
        <div className="mt-2 flex items-center gap-2">
          {p.reviews > 0 ? (
            <>
              <Stars value={p.rating} size={14} />
              <span className="text-xs text-navy/50">{p.rating.toFixed(1)} ({p.reviews})</span>
            </>
          ) : (
            <span className="rounded-full bg-sky px-2 py-0.5 text-[11px] font-bold text-navy">New arrival</span>
          )}
        </div>
        <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-navy/60">{p.blurb}</p>
        <ul className="mt-3 space-y-1">
          {p.highlights.map((h) => (
            <li key={h} className="flex items-center gap-1.5 text-xs font-medium text-navy/70">
              <Check size={13} className="text-brand" /> {h}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-heading text-xl font-extrabold text-navy">{formatPrice(p.price)}</span>
          {p.compareAt && (
            <span className="text-sm text-navy/40 line-through">{formatPrice(p.compareAt)}</span>
          )}
        </div>
        <button
          onClick={() => addItem({ id: p.id, name: p.name, price: p.price, image: p.image })}
          className="mt-3 flex items-center justify-center gap-2 rounded-full bg-navy py-2.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand"
        >
          <ShoppingCart size={15} /> Add to Cart
        </button>
      </div>
    </div>
  );
}

export default function TopSellers() {
  const ref = useReveal();
  return (
    <section id="top-sellers" className="bg-white py-16">
      <div ref={ref} className="reveal mx-auto max-w-7xl px-4 lg:px-8">
        <div className="mb-8 flex flex-col items-center text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Customer favourites</p>
          <h2 className="mt-1 font-heading text-3xl font-extrabold text-navy md:text-4xl">Our Top Sellers</h2>
          <p className="mt-2 max-w-2xl text-sm text-navy/60">
            The gear Australians keep coming back for — real products, real reviews, real value.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {topSellers.map((p) => (
            <TopSellerCard key={p.id} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
